import { Injectable } from '@angular/core';
import { map } from 'rxjs/operators';
import { Observable, Subscription } from 'rxjs';
import {
  GbGeschaeftId,
  GbpersonService,
  GbstandardrechtService,
  GrundstueckId,
  HistInfoMeta,
  LastRechtGrundstueckEntryMeta,
  LastRechtGruppeEntryMeta,
  LastRechtPersonEntryMeta,
  LoadStandardrechtSichtGrundstueckResponseMeta,
  PersonGbMasterDetailEntryMeta,
  PersonStammMasterId,
  RangregelungEntryMeta,
  RechteBeteiligungDetailEntryMeta,
  RechtsgrundEntryMeta,
  StandardRechtBeteiligungMasterDetailEntryMeta,
  StandardrechtBeteiligungMasterId,
  StandardrechtMasterDetailEntryMeta,
  StandardrechtVersionDetailEntryMeta
} from '../gb-api';
import { TimepointCtx, TimepointService } from './timepoint.service';
import { AppConfigService } from './app-config.service';
import { TranslateService } from '@ngx-translate/core';
import { ComparatorService } from '../../shared/services/comparator-service';
import { HistoryMode } from './HistoryMode';
import { calculateParallelHistInfos, calculateSerialHistInfos } from '../../shared/functions/history.functions';
import { InfinitLookupResult } from '../../shared/components/list-loader/infinit-lookup-result';
import { PersonStammMasterDetailEntryMeta, PersonstammpersonService } from '../personstamm-api';

@Injectable()
export class StandardrechtViewmodelService {

  constructor(private timepointService: TimepointService,
              private appConfigService: AppConfigService,
              private translateService: TranslateService,
              private rechtService: GbstandardrechtService,
              private gbpersonService: GbpersonService,
              private comparatorService: ComparatorService,
              private personstammService: PersonstammpersonService) {
  }

  loadSichtByGbGeschaeftId(id: GbGeschaeftId,
                           ids: Array<GrundstueckId>,
                           subscriptions: Array<Subscription>,
                           rechtarten: Array<'DIENSTBARKEIT' | 'VORMERKUNG' | 'ANMERKUNG' | 'GRUNDLAST' | 'PFANDRECHT' | 'SDR_DIENSTBARKEIT' | 'EIGENTUM_ANTEIL'>): Observable<LastRechtRootEntryVm[]> {
    return this.rechtService.loadStandardrechtSichtGeschaeft(id.id, rechtarten, false)
      .pipe(
        map(response => {
          const res = new Array<LastRechtRootEntryVm>();
          for (const entry of response.entries) {
            res.push(...this.createViewModelByGrundstuecke(entry, response.grundstueckIds, subscriptions, false));
          }
          return res;
        })
      );
  }

  loadGbMeldungsSichtByGbGeschaeftId(id: GbGeschaeftId,
                                     ids: Array<GrundstueckId>,
                                     subscriptions: Array<Subscription>,
                                     rechtarten: Array<'DIENSTBARKEIT' | 'VORMERKUNG' | 'ANMERKUNG' | 'GRUNDLAST' | 'PFANDRECHT' | 'SDR_DIENSTBARKEIT' | 'EIGENTUM_ANTEIL'>): Observable<LastRechtRootEntryVm[]> {
    return this.rechtService.loadStandardrechtSichtGbMeldung(id.id, rechtarten, true)
      .pipe(
        map(response => {
          const res = new Array<LastRechtRootEntryVm>();
          for (const entry of response.entries) {
            res.push(...this.createViewModelByGrundstuecke(entry, response.grundstueckIds, subscriptions, false));
          }
          return res;
        })
      );
  }

  loadSichtByGrundstueckId(id: GrundstueckId,
                           rechtart: 'DIENSTBARKEIT' | 'VORMERKUNG' | 'ANMERKUNG' | 'GRUNDLAST' | 'PFANDRECHT' | 'SDR_DIENSTBARKEIT' | 'EIGENTUM_ANTEIL',
                           offset: number,
                           limit: number,
                           subscriptions: Array<Subscription>,
                           mitAddressen: boolean = false): Observable<StandardrechtSichtGrundstueckVm> {
    const z = this.timepointService.timepointCtx;
    return this.rechtService.loadStandardrechtSichtGrundstueck(id.id,
      rechtart,
      false,
      z.timepointIso,
      z.timepointId,
      this.appConfigService.einstellungHistorischeDaten,
      offset,
      limit)
      .pipe(
        map(r => {
          const entries = new Array<LastRechtRootEntryVm>();
          r.entries.forEach(e => {
              const lastRechtRootEntryVms = this.createViewModelByGrundstueck(e, id, subscriptions, mitAddressen);
              entries.push(...lastRechtRootEntryVms);
            }
          );
          const infinitiveLookupResult = {
            result: entries,
            lastJunk: r.lastJunk
          };
          return new StandardrechtSichtGrundstueckVm(infinitiveLookupResult, r);
        }));
  }

  loadGbMeldungsSichtByGrundstueckId(id: GrundstueckId,
                                     rechtart: 'DIENSTBARKEIT' | 'VORMERKUNG' | 'ANMERKUNG' | 'GRUNDLAST' | 'PFANDRECHT' | 'SDR_DIENSTBARKEIT' | 'EIGENTUM_ANTEIL',
                                     offset: number,
                                     limit: number,
                                     subscriptions: Array<Subscription>,
                                     mitAddressen: boolean = false): Observable<StandardrechtSichtGrundstueckVm> {
    const z = this.timepointService.timepointCtx;
    return this.rechtService.loadStandardrechtSichtGbMeldung1(id.id,
      rechtart,
      false,
      z.timepointIso,
      z.timepointId,
      this.appConfigService.einstellungHistorischeDaten,
      offset,
      limit)
      .pipe(
        map(r => {
          const entries = new Array<LastRechtRootEntryVm>();
          r.entries.forEach(e => {
              const lastRechtRootEntryVms = this.createViewModelByGrundstueck(e, id, subscriptions, mitAddressen);
              entries.push(...lastRechtRootEntryVms);
            }
          );
          const infinitiveLookupResult = {
            result: entries,
            lastJunk: r.lastJunk
          };
          return new StandardrechtSichtGrundstueckVm(infinitiveLookupResult, r);
        }));
  }

  private createViewModelByGrundstuecke(recht: StandardrechtMasterDetailEntryMeta,
                                        ids: Array<GrundstueckId>,
                                        subscriptions: Array<Subscription>,
                                        mitAddressen: boolean): LastRechtRootEntryVm[] {
    const res = new Array<LastRechtRootEntryVm>();
    for (const id of ids) {
      res.push(...this.createViewModelByGrundstueck(recht, id, subscriptions, mitAddressen));
    }
    return res;
  }

  private createViewModelByGrundstueck(recht: StandardrechtMasterDetailEntryMeta, id: GrundstueckId, subscriptions: Array<Subscription>, mitAddressen: boolean): LastRechtRootEntryVm[] {
    const res = new Array<LastRechtRootEntryVm>();
    const lastRechtGruppen = this.groupLastRechtGruppenByBelastungsArt(recht, id);
    lastRechtGruppen.forEach((v) => {
      const timpoint = this.timepointService.timepointCtx;
      const lastRechtRootEntryVm = new LastRechtRootEntryVm(recht, v, id, this.getViewModelCtx(timpoint, subscriptions, mitAddressen));
      res.push(lastRechtRootEntryVm);
    });
    return res;
  }

  private groupLastRechtGruppenByBelastungsArt(recht: StandardrechtMasterDetailEntryMeta, grundstueckId: GrundstueckId) {
    const res = new Map<String, Array<LastRechtGruppeEntryMeta>>();
    for (const lastRechtGruppe of recht.lastRechtGruppen) {
      const grundstueckEntries = extractSelfReferences([lastRechtGruppe], grundstueckId);
      if (!grundstueckEntries || grundstueckEntries.length === 0) {
        continue;
      }
      const belastungsArt = grundstueckEntries[0].belastungArt;
      const key = JSON.stringify({
        id: grundstueckEntries[0].standardrechtBeteiligungMasterId,
        belastungsArt: belastungsArt
      });
      let list = res.get(key);
      if (!list) {
        list = new Array<LastRechtGruppeEntryMeta>();
      }
      list.push(lastRechtGruppe);
      res.set(key, list);
    }
    return res;
  }

  private getViewModelCtx(timpoint: TimepointCtx, subscriptions: Array<Subscription>, mitAddressen: boolean) {
    return <ViewModelCtx>{
      timepointId: timpoint.timepointId,
      timepointIso: timpoint.timepointIso,
      einstellungHistorischeDaten: this.appConfigService.einstellungHistorischeDaten,
      translateService: this.translateService,
      personService: this.gbpersonService,
      personstammService: this.personstammService,
      comparatorService: this.comparatorService,
      mitAdressen: mitAddressen,
      subscriptions: subscriptions
    };
  }

}

export interface ViewModelCtx {

  translateService: TranslateService;
  personService: GbpersonService;
  comparatorService: ComparatorService;
  einstellungHistorischeDaten: HistoryMode.HistoryMode;
  timepointIso: string;
  timepointId: string;
  mitAdressen: boolean;
  personstammService: PersonstammpersonService;
  subscriptions: Array<Subscription>;

}

export class StandardrechtSichtGrundstueckVm {

  private readonly response: LoadStandardrechtSichtGrundstueckResponseMeta;

  constructor(public entries: InfinitLookupResult<LastRechtRootEntryVm>, response: LoadStandardrechtSichtGrundstueckResponseMeta) {
    this.response = response;
    this.entries = entries;
  }

  get ersterfassungUebergeben() {
    return this.response.ersterfassungUebergeben;
  }

}

export class LastRechtRootEntryVm {

  belastungsArt: LastRechtGrundstueckEntryMeta.BelastungArtEnum;
  standardRechtMasterVm: StandardrechtMasterVm;
  oppositeGrundstuckEntries: LastRechtGrundstueckEntryVm[];
  oppositePersonEntries: LastRechtPersonEntryVm[];
  rechtebeziehungen: RechteBeziehungDetailVm[];
  selfReferences: LastRechtGrundstueckEntryMeta[];
  rangregelungen: RangregelungDetailVm[];

  constructor(public object: StandardrechtMasterDetailEntryMeta,
              private lastRechtGruppen: LastRechtGruppeEntryMeta[],
              public grundstueckId: GrundstueckId,
              private ctx: ViewModelCtx) {
    this.selfReferences = extractSelfReferences(lastRechtGruppen, this.grundstueckId);
    const histInfo = calculateParallelHistInfos(this.selfReferences.map(r => r.histInfo));

    this.belastungsArt = this.selfReferences[0].belastungArt;
    this.oppositeGrundstuckEntries = this.calculateOppositeEntries(this.lastRechtGruppen, this.selfReferences);
    this.standardRechtMasterVm = new StandardrechtMasterVm(object, histInfo);
    this.rechtebeziehungen = this.calculateRechtebeziehungen();
    this.rangregelungen = this.calculateRangregelungen();
    this.calculateOppositePersonEntries(ctx);
  }

  public get rechtsgruende(): RechtsgrundEntryMeta[] {
    if (!(this.belastungsArt === 'BELASTET' || this.oppositeGrundstuckEntries.length === 0)) {
      return Array<RechtsgrundEntryMeta>();
    }
    const rechtsgruende = new Array<RechtsgrundEntryMeta>();
    for (const selfReference of this.selfReferences) {
      rechtsgruende.push(...selfReference.rechtsgruende);
    }
    return rechtsgruende;
  }

  public get isBelastet(): boolean {
    return this.belastungsArt === 'BELASTET';
  }

  public get belastungsartKurz(): String {
    switch (this.belastungsArt) {
      case 'BELASTET':
        return 'Belastet';
      case 'BERECHTIGT' :
        return 'Berechtigt';
      case'BELASTET_BERECHTIGT' :
        return 'BelastetBerechtigt';
      case'DOMINANTINDET':
        return 'DominantIndet';
      default:
        return '';
    }
  }


  private static calculateEnforcedRechtsgruende(belastungsArt: LastRechtGrundstueckEntryMeta.BelastungArtEnum,
                                                selfReferences: LastRechtGrundstueckEntryMeta[]): RechtsgrundEntryMeta[] {
    if (belastungsArt !== 'BERECHTIGT') {
      return undefined;
    }
    const rechtsgruende = new Array<RechtsgrundEntryMeta>();
    for (const selfReference of selfReferences) {
      rechtsgruende.push(...selfReference.rechtsgruende);
    }
    return rechtsgruende;
  }

  private static calculateEnforcesHistInfo(belastungsArt: LastRechtGrundstueckEntryMeta.BelastungArtEnum,
                                           selfReferences: LastRechtGrundstueckEntryMeta[]): HistInfoMeta {
    if (belastungsArt !== 'BERECHTIGT') {
      return undefined;
    }
    return selfReferences[0].histInfo;
  }

  private calculateRechtebeziehungen(): RechteBeziehungDetailVm[] {
    let res = new Array<RechteBeziehungDetailVm>();
    for (const beteiligung of this.relevantStandardrechtBeteiligungen()) {
      for (const rechBez of beteiligung.object.rechteBeziehungen) {
        const selfRef = rechBez.grundstueck.id.id === this.grundstueckId.id;
        res.push(new RechteBeziehungDetailVm(rechBez, selfRef));
      }
    }
    res = res.filter(b => b.object.recht && b.object.recht.id.id !== this.standardRechtMasterVm.object.id.id);
    return res;
  }

  private calculateRangregelungen(): RangregelungDetailVm[] {
    const res = new Array<RangregelungDetailVm>();
    for (const beteiligung of this.relevantStandardrechtBeteiligungen()) {
      for (const rangregelung of beteiligung.object.rangregelung) {
        if (rangregelung.vorgaengerMaster) {
          res.push(new RangregelungDetailVm(rangregelung, rangregelung.grundstueck.id.id === this.grundstueckId.id));
        }
      }
    }
    return res;
  }

  private relevantStandardrechtBeteiligungen(): StandardrechtBeteiligungMasterVm[] {
    const ids = this.selfReferences.map(r => r.standardrechtBeteiligungMasterId);
    return this.standardRechtMasterVm.getRelevantStandardrechtMaster(ids);
  }

  private calculateOppositePersonEntries(ctx: ViewModelCtx) {
    this.oppositePersonEntries = [];
    for (const lastRechtGruppe of this.lastRechtGruppen) {
      for (const lastRechtPersonEntryMeta of lastRechtGruppe.personen) {
        const sub = ctx.personService.loadPersonGbById(
          ctx.einstellungHistorischeDaten,
          lastRechtPersonEntryMeta.personGbMasterId ? lastRechtPersonEntryMeta.personGbMasterId.id : null,
          lastRechtPersonEntryMeta.personGbVersionId ? lastRechtPersonEntryMeta.personGbVersionId.id : null,
          ctx.timepointIso,
          ctx.timepointId)
          .subscribe(p => {
            const entry = new LastRechtPersonEntryVm(lastRechtPersonEntryMeta, p, ctx);
            this.oppositePersonEntries = [...this.oppositePersonEntries, entry];
          });
        ctx.subscriptions.push(sub);
      }
    }
  }

  private calculateOppositeEntries(lastRechtGruppen: LastRechtGruppeEntryMeta[],
                                   selfReferences: LastRechtGrundstueckEntryMeta[]): LastRechtGrundstueckEntryVm[] {
    const res = Array<LastRechtGrundstueckEntryVm>();
    const belastungArt = selfReferences[0].belastungArt;
    for (const lastRechtGruppe of lastRechtGruppen) {
      const selfReferencesInLastRechtGruppe = extractSelfReferences([lastRechtGruppe], this.grundstueckId);
      const enforcedRechtsgruende = LastRechtRootEntryVm.calculateEnforcedRechtsgruende(belastungArt, selfReferencesInLastRechtGruppe);
      const enforcedHistInro = LastRechtRootEntryVm.calculateEnforcesHistInfo(belastungArt, selfReferencesInLastRechtGruppe);
      if (belastungArt === 'BELASTET') {
        res.push(...this.createEntries(lastRechtGruppe.grundstueckeRecht, selfReferences, enforcedRechtsgruende, enforcedHistInro));
      } else if (belastungArt === 'BERECHTIGT') {
        res.push(...this.createEntries(lastRechtGruppe.grundstueckeLast, selfReferences, enforcedRechtsgruende, enforcedHistInro));
      } else if (belastungArt === 'BELASTET_BERECHTIGT') {
        res.push(...this.createEntries(lastRechtGruppe.grundstueckeLastRecht, selfReferences, enforcedRechtsgruende, enforcedHistInro));
      }
    }
    return res;
  }

  private createEntries(lastRechtGrundstueckEntries: LastRechtGrundstueckEntryMeta[],
                        selfReferences: LastRechtGrundstueckEntryMeta[],
                        enforcedRechtsgruende: RechtsgrundEntryMeta[],
                        enforcedHistInfo: HistInfoMeta) {
    return lastRechtGrundstueckEntries
      .filter(e => !selfReferences.includes(e))
      .map(g => new LastRechtGrundstueckEntryVm(g, enforcedRechtsgruende, enforcedHistInfo));
  }

}

export class LastRechtGrundstueckEntryVm {

  constructor(public object: LastRechtGrundstueckEntryMeta, //
              private enforcedRechtsgruende: LastRechtGruppeEntryMeta[], //
              private enforcedHistInfo: HistInfoMeta) {

  }

  public get rechtsgruende(): RechtsgrundEntryMeta[] | LastRechtGruppeEntryMeta[] {
    if (this.enforcedRechtsgruende) {
      return this.enforcedRechtsgruende;
    }
    return this.object.rechtsgruende;
  }

  public get belastungsartKurz(): String {
    switch (this.object.belastungArt) {
      case 'BELASTET':
        return 'ZuLasten';
      case 'BERECHTIGT' :
        return 'ZuGunsten';
      case'BELASTET_BERECHTIGT' :
        return 'ZuGunstenLasten';
      case'DOMINANTINDET':
        return '';
      default:
        return '';
    }
  }

  public get histInfo(): HistInfoMeta {
    if (this.enforcedHistInfo) {
      return this.enforcedHistInfo;
    }
    return this.object.histInfo;
  }

}

export class LastRechtPersonEntryVm {

  personStamm: PersonStammMasterDetailEntryMeta;
  isAuskunftGesperrt: boolean;
  isAddresseGesperrt: boolean;

  constructor(public object: LastRechtPersonEntryMeta, public person: PersonGbMasterDetailEntryMeta, ctx: ViewModelCtx) {
    if (ctx.mitAdressen) {
      this.loadPersonstamm(person.personstammMasterId, ctx);
    }
  }


  private loadPersonstamm(personStammMaterId: PersonStammMasterId, ctx: ViewModelCtx) {
    if (personStammMaterId) {
      ctx.personstammService.loadById(personStammMaterId.id, HistoryMode.HistoryMode.OHNE_HIST, null, ctx.timepointIso, null)
        .subscribe(entry => {
          this.personStamm = entry;
          this.isAuskunftGesperrt = entry.auskunftsperre === PersonStammMasterDetailEntryMeta.AuskunftsperreEnum.AUSKUNFT_SPERRE;
          this.isAddresseGesperrt = entry.auskunftsperre === PersonStammMasterDetailEntryMeta.AuskunftsperreEnum.ADRESS_SPERRE;
        });
    }
  }
}

export class StandardrechtMasterVm {

  versionenVm: StandardrechtVersionVm[];
  beteiligungsMastersVm: StandardrechtBeteiligungMasterVm[];

  constructor(public object: StandardrechtMasterDetailEntryMeta, private rechteBeziehungHistInfo: HistInfoMeta) {
    this.versionenVm = new Array<StandardrechtVersionVm>();
    for (let i = 0; i < object.versions.length; i++) {
      const isLastVersion = i === object.versions.length - 1;
      this.versionenVm.push(new StandardrechtVersionVm(object.versions[i], object, this.rechteBeziehungHistInfo, isLastVersion));
    }
    this.beteiligungsMastersVm = object.beteiligungMasters.map(b => new StandardrechtBeteiligungMasterVm(b));
  }

  get lastVersion(): StandardrechtVersionVm {
    return this.versionenVm[this.versionenVm.length - 1];
  }

  getRelevantStandardrechtMaster(masterIds: Array<StandardrechtBeteiligungMasterId>): StandardrechtBeteiligungMasterVm[] {
    const masterIdsAsString = masterIds.map(i => i.id);
    return this.beteiligungsMastersVm.filter(e => masterIdsAsString.includes(e.object.id.id));
  }

}

export class StandardrechtVersionVm {

  constructor(public object: StandardrechtVersionDetailEntryMeta,
              private master: StandardrechtMasterDetailEntryMeta,
              private rechteBeziehungHistInfo: HistInfoMeta,
              private lastVersion: boolean) {
  }

  get hasOeffentlicheOrNichtOeffentlicheBemerkungen(): boolean {
    return !!(this.object.bemerkung || this.object.bemerkungNichtOeffentlich);
  }

  get hasNichtOeffentlicheBemerkungen(): boolean {
    return !!(this.object.bemerkungNichtOeffentlich);
  }

  get histInfo(): HistInfoMeta {
    if (this.lastVersion) {
      return calculateSerialHistInfos([this.object.histInfo, this.rechteBeziehungHistInfo]);
    } else {
      return this.object.histInfo;
    }
  }

  getQueryParams() {
    return { id: this.master.id.id, versionId: this.object.id.id };
  }

}

export class StandardrechtBeteiligungMasterVm {

  constructor(public object: StandardRechtBeteiligungMasterDetailEntryMeta) {
  }

}

export class RechteBeziehungDetailVm {

  constructor(public object: RechteBeteiligungDetailEntryMeta, public isSelfReference: boolean) {
  }

}

export class RangregelungDetailVm {

  constructor(public object: RangregelungEntryMeta, public isSelfReference: boolean) {
  }

}

function extractSelfReferences(lastRechtGruppen: LastRechtGruppeEntryMeta[], grundstueckId: GrundstueckId): LastRechtGrundstueckEntryMeta[] {
  const res = new Array<LastRechtGrundstueckEntryMeta>();
  for (const lastRechtGruppe of lastRechtGruppen) {
    res.push(...extractLastRechtEntriesForGrundstueck(lastRechtGruppe.grundstueckeLast, grundstueckId));
    res.push(...extractLastRechtEntriesForGrundstueck(lastRechtGruppe.grundstueckeRecht, grundstueckId));
    res.push(...extractLastRechtEntriesForGrundstueck(lastRechtGruppe.grundstueckeLastRecht, grundstueckId));
  }
  return res;
}

function extractLastRechtEntriesForGrundstueck(entries: Array<LastRechtGrundstueckEntryMeta>, grundstueckId: GrundstueckId): LastRechtGrundstueckEntryMeta[] {
  return entries.filter(gl => gl.grundstueck?.id.id === grundstueckId.id);
}
